<script>
const mapTypeComponents = {
  [TAB_VALUE.CONVERSATIONS]: defineAsyncComponent(
    () => import("@/components/InteractionConversations.vue"),
  ),
  [TAB_VALUE.DRAFTS]: defineAsyncComponent(
    () => import("@/components/InteractionDrafts.vue"),
  ),
  [TAB_VALUE.HISTORY]: defineAsyncComponent(
    () => import("@/components/InteractionHistory.vue"),
  ),
  [TAB_VALUE.COMMENTS]: defineAsyncComponent(
    () => import("@/components/InteractionComments.vue"),
  ),
};

</script>
<script setup>
import {
  TAB_VALUE,
  TAB_VALUE_LABEL,
} from "@/constants/interaction";
import { capitalizeFirstLetter, objectToQueryString } from "@/utils/utils";
import { useUserStore } from "@/stores/user";
import { useInteractionDetailStore } from "@/stores/interactionDetail";
import ListLoader from "./ui-kit/base/ListLoader.vue";
import { TicketCategoryStatusEnum } from "@/utils/constants";
import { useNavigation } from "@/composables/useNavigation";
import { useTicketResponses } from "@/composables/useTicketResponses";
import { INTERNAL_COMMENTS_KEY, useInternalComments } from "@/composables/useInternalComments";

const thread = inject("THREAD")
const ticketInteraction = inject("TICKET_INTERACTION")
const isEndUser = inject("READ_ONLY_MODE")

const userStore = useUserStore()
const interactionDetailsStore = useInteractionDetailStore()
const { queryParams, patchQueryParams } = useNavigation()
const {
  ticketResponses,
  lastTicketResponse,
  count: ticketResponsesCount,
  isLoading: isTicketResponseLoading,
  getTicketResponses,
  draftTicketResponses,
  draftCount: draftTicketCount,
  currentDraftResponse,
  getDraftTicketResponses,
  createTicketResponse,
  updateTicketResponse,
} = useTicketResponses(thread)
const internalComments = useInternalComments(thread)
const { count: internalCommentsCount } = internalComments
provide(INTERNAL_COMMENTS_KEY, internalComments)

// const activeTab = ref(null)

// onMounted(async () => {
//   if (!ticketInteraction.value || !ticketInteraction.value?.id) {
//     activeTab.value = TAB_VALUE.COMMENTS
//     return
//   }
  
//   activeTab.value = null

//   await interactionDetailsStore.load(ticketInteraction.value?.id)
//   // interactionDetailsStore.isEditorOpen = true

//   // initialize active tab only once
//   if (!activeTab.value) {
//     activeTab.value = visibleTabObjs.value[0]?.value
//   }
// }, { immediate: true })

// proxy value
const activeTab = computed({
  get() {
    console.log("firing activeTab change")
    const tab = queryParams.value?.tab
    // If it is in the visible tab, return it, else return the first one
    const tabObj = visibleTabObjs.value.find(t => t.value === tab) || visibleTabObjs.value[0]
    return tabObj?.value || TAB_VALUE.COMMENTS
  },
  set(tab) {
    patchQueryParams({ tab })
  },
})

// Array of allowable tabs
const visibleTabObjs = computed(() => {
    const tabs = []

    if(!isEndUser.value) {
      tabs.push(TAB_VALUE.COMMENTS, TAB_VALUE.HISTORY)
    }

    if(Object.values(TicketCategoryStatusEnum).includes(thread.value.status)) {
        const addTabs = isEndUser.value ? [TAB_VALUE.CONVERSATIONS] : [TAB_VALUE.CONVERSATIONS, TAB_VALUE.DRAFTS]
        tabs.unshift(...addTabs)
    }

    const getTabLabel = (tab) => {
      if (tab === TAB_VALUE.CONVERSATIONS) {
        return "Emails";
        if (readOnlyMode.value) {
          return "Emails (View Only)";
        }
        else {
          return "Emails";
        }
      }
      return TAB_VALUE_LABEL[tab] || capitalizeFirstLetter(tab);
    };

    return tabs.map((tab) => ({
        value: tab,
        label: getTabLabel(tab),
        slotName: tab.replace(" ", "-").toLowerCase(),
    }))
});

provide("TICKET_RESPONSES", ticketResponses)
provide("TICKET_RESPONSES_REFRESH", getTicketResponses)
provide("LAST_TICKET_RESPONSE", lastTicketResponse)
provide("TICKET_RESPONSES_LOADING", isTicketResponseLoading)
provide("TICKET_RESPONSES_COUNT", ticketResponsesCount)
provide("DRAFT_TICKET_RESPONSES", draftTicketResponses)
provide("DRAFT_TICKET_RESPONSES_REFRESH", getDraftTicketResponses)
provide("DRAFT_TICKET_RESPONSES_COUNT", draftTicketCount)
provide("CURRENT_DRAFT_RESPONSE", currentDraftResponse)
provide("TICKET_RESPONSE_CREATE", createTicketResponse)
provide("TICKET_RESPONSE_UPDATE", updateTicketResponse)
provide("ACTIVE_TAB", activeTab)
</script>

<template>
  <div class="split-right-view" v-if="thread">
    <DynamicTab
      class="tabs-wrapper"
      v-model="activeTab"
      :tabs="visibleTabObjs"
    >
      <!-- Slot names for templates below MUST match tab.value, else it will fallback
       to tab.label, and your template contents get silently ignored. -->
      <template #conversations="{ tab }">
        {{ tab.label }}
        <span v-if="ticketResponsesCount">
          ({{ ticketResponsesCount }})
        </span>
      </template>
      <template #drafts="{ tab }">
        {{ tab.label }}
        <span v-if="draftTicketCount">
          ({{ draftTicketCount }})
        </span>
      </template>
      <template #internal-comments="{ tab }">
        {{ tab.label }}
        <span v-if="internalCommentsCount">
          ({{ internalCommentsCount }})
        </span>
      </template>
      <template #thread-history="{ tab }">
        {{ tab.label }}
      </template>
      <template #ticket-history="{ tab }">
        {{ tab.label }}
      </template>
    </DynamicTab>
    <div
      class="tab-content"
      :class="{ [activeTab]: true }"
    >
      <component :is="mapTypeComponents[activeTab]" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.split-right-view {
  position: relative;
  display: flex;
  flex-direction: column;
  background: $neutral-0;
  border-radius: 8px;
  border: 1px solid $neutral-150;
  height: 100%;
  overflow: hidden;
  min-height: 500px;
}

.tabs-wrapper {
  flex-shrink: 0;
  border-bottom: 1px solid $bluish-gray-600;
}

.tab-content {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: auto;

  &.CONVERSATIONS {
    overflow: auto;
  }
}
</style>
